/**
 * Activities a volunteer can sign up for.
 *
 * The client document describes volunteers choosing a community to attend each
 * quarter, so choosing is the point — this is not a request queue. Signing up
 * creates the same roster place a coordinator would have created, and staff can
 * still see and cancel it.
 *
 * What is deliberately absent: any sign of who else is going. This is the one
 * screen where a volunteer reads about work that is not theirs yet, and the API
 * returns counts rather than names for exactly that reason.
 *
 * Mandatory training lives HERE now, not on the registration form (12 Sep 2026).
 * A volunteer must open the training document(s) and confirm they have read them
 * before each sign-up — the gate is rebuilt fresh every time the "Sign up" button
 * is pressed, so it can never carry an "already opened" tick from a previous
 * sign-up or a previous volunteer on a shared device.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
} from '../../components/common';
import { useToast } from '../../components/common/Toast';
import { useEnrolInSession, useOpenSessions } from '../../hooks/useSessions';
import { useTrainingMaterials } from '../../hooks/useTrainingMaterials';
import type { Session } from '../../services/api/sessions';
import { formatDate } from '../../utils/format';
import styles from './MySessionsPage.module.css';

interface TrainingMaterial {
  label: string;
  url: string;
}

function whenText(session: Session): string {
  if (!session.end_date || session.end_date === session.start_date) return formatDate(session.start_date);
  return `${formatDate(session.start_date)} – ${formatDate(session.end_date)}`;
}

function OpportunityRow({ session, materials }: { session: Session; materials: TrainingMaterial[] }) {
  const enrol = useEnrolInSession();
  const toast = useToast();
  const [taken, setTaken] = useState(false);

  // The training gate for THIS sign-up. Opening it resets both, so a volunteer
  // reads the material afresh before every sign-up (12 Sep 2026).
  const [gateOpen, setGateOpen] = useState(false);
  const [opened, setOpened] = useState<Set<string>>(() => new Set());
  const [acknowledged, setAcknowledged] = useState(false);

  // Nothing to open (none configured, or the list failed to load) leaves the
  // acknowledgement standing on its own, exactly as the old form did.
  const allOpened = materials.length === 0 || materials.every((m) => opened.has(m.url));
  const canConfirm = allOpened && acknowledged;

  const startSignUp = () => {
    setOpened(new Set());
    setAcknowledged(false);
    setGateOpen(true);
  };

  const confirmSignUp = () => {
    enrol.mutate(session.session_id, {
      onSuccess: () => {
        // Held locally as well as invalidating the list: the row leaves the
        // list on refetch, and a button that says "Sign up" right up until the
        // row vanishes reads as though nothing happened.
        setTaken(true);
        setGateOpen(false);
        toast.success('You are signed up. It will appear under My volunteering.');
      },
      onError: (error) => toast.error(error, 'Could not sign you up.'),
    });
  };

  return (
    <li className={styles.row}>
      {/*
        The card's detail is a link; the Sign up button beside it is not part of
        it (9 Oct 2026). Making the whole row clickable was the ask, but a row
        that both navigates and contains a button has one target swallowing the
        other — and the one that gets swallowed is always the one somebody meant
        to press. So the link covers everything a person reads, and the action
        keeps its own edges.
      */}
      <Link to={`/opportunities/${session.session_id}`} className={styles.cardLink}>
        <span className={styles.name}>{session.session_name}</span>
        <span className={styles.meta}>
          {session.activity_name} · {session.program_name}
        </span>
        <span className={styles.meta}>
          {whenText(session)}
          {session.assigned_count > 0 &&
            ` · ${session.assigned_count} ${
              session.assigned_count === 1 ? 'volunteer' : 'volunteers'
            } so far`}
        </span>
        <span className={styles.more}>See what this involves →</span>
      </Link>

      <div className={styles.side}>
        {taken ? (
          <span className={styles.done}>Signed up</span>
        ) : gateOpen ? (
          <div className={styles.confirmBox}>
            <p className={styles.confirmAsk}>
              Please read the mandatory training before you sign up.
            </p>

            {materials.length > 0 && (
              <ul className={styles.trainingList}>
                {materials.map((material) => (
                  <li key={material.url}>
                    <a
                      href={material.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      // Opening a document is what unlocks the confirmation below,
                      // so "I have read this" cannot be ticked for something never
                      // opened.
                      onClick={() => setOpened((current) => new Set(current).add(material.url))}
                    >
                      {material.label}
                    </a>
                    {opened.has(material.url) && <span className={styles.opened}>✓ opened</span>}
                  </li>
                ))}
              </ul>
            )}

            <label className={styles.acknowledge}>
              <input
                type="checkbox"
                checked={acknowledged}
                disabled={!allOpened}
                onChange={(event) => setAcknowledged(event.target.checked)}
              />
              <span>I confirm I have read the mandatory training material.</span>
            </label>
            {!allOpened && <p className={styles.hint}>Open each document above to enable this.</p>}

            <div className={styles.confirmActions}>
              <Button
                variant="primary"
                onClick={confirmSignUp}
                disabled={!canConfirm || enrol.isPending}
              >
                {enrol.isPending ? 'Signing up…' : 'Confirm sign up'}
              </Button>
              <Button
                variant="secondary"
                onClick={() => setGateOpen(false)}
                disabled={enrol.isPending}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="primary" onClick={startSignUp}>
            Sign up
          </Button>
        )}
      </div>
    </li>
  );
}

export function OpportunitiesPage() {
  const open = useOpenSessions();
  const trainingMaterials = useTrainingMaterials();

  if (open.isPending) return <LoadingState label="Loading what is coming up…" />;
  if (open.isError) {
    return (
      <>
        <PageHeader title="Find activities" />
        <Card>
          <ErrorState error={open.error} onRetry={() => void open.refetch()} />
        </Card>
      </>
    );
  }

  const rows = open.data ?? [];
  // A failed load leaves the acknowledgement standing on its own — the same
  // gentle degradation the registration form used (see useTrainingMaterials).
  const materials = trainingMaterials.data ?? [];

  return (
    <>
      <PageHeader title="Find activities" />

      <Card>
        {rows.length === 0 ? (
          <EmptyState
            title="Nothing open just now"
            message="Nothing open to sign up for yet."
          />
        ) : (
          <ul className={styles.list}>
            {rows.map((session) => (
              <OpportunityRow key={session.session_id} session={session} materials={materials} />
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}
